import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Alert } from "react-daisyui";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as z from "zod";

import Routes from "@/router/Routes.ts";
import { useAuthContext } from "@/context/AuthContext.tsx";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import OtpInput from "@/components/functional/OtpInput/OtpInput.tsx";
import SearchParams from "@/_constants/SearchParams.ts";
import { formatErrorMessage } from "@/utils/formatErrorMessage.ts";
import RegistrationAccountStatus from "@/_constants/RegistrationAccountStatus.ts";
import { useRegistrationContext } from "@/context/registrationFlowContext.ts";
import { useToast } from "@/context/ToastContext.tsx";

const RESEND_CODE_COOLDOWN_SECONDS = 60;

const confirmEmailSchema = z.object({
  email: z
    .string()
    .min(1, {
      message:
        "Invalid email. Try again using link from email that you've received.",
    })
    .email("Invalid email. Contact support."),
  code: z.string().min(1, { message: "This field is required." }),
});

type ConfirmEmailSchema = z.infer<typeof confirmEmailSchema>;

const ConfirmEmailPage: FC = () => {
  const [searchParams] = useSearchParams();
  const { draftUser, token, setStatus, clearFlow } = useRegistrationContext();
  const [resendCooldown, setResendCooldown] = useState(
    RESEND_CODE_COOLDOWN_SECONDS,
  );

  const email = draftUser?.email ??
    decodeURIComponent(searchParams.get(SearchParams.EMAIL) ?? "");

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmEmailSchema>({
    resolver: zodResolver(confirmEmailSchema),
    defaultValues: {
      email,
      code: "",
    },
  });
  const { t } = useTranslation();
  const { addToast } = useToast();
  const {
    verifyEmailQuery: {
      mutateAsync: verifyEmail,
      isPending: isLoadingVerifyEmail,
      error: errorVerifyEmail,
      isError: isErrorVerifyEmail,
    },
    resendVerificationCodeQuery: {
      mutateAsync: resendVerificationCode,
      isPending: isLoadingVerificationCode,
      error: errorVerificationCode,
      isError: isErrorVerificationCode,
      isSuccess: isSuccessVerificationCode,
    },
  } = useAuthContext();

  const navigate = useNavigate();

  useEffect(() => {
    if (resendCooldown === 0) return;

    const timeout = window.setTimeout(() => {
      setResendCooldown((seconds) => Math.max(seconds - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [resendCooldown]);

  const handleVerifyEmail = (data: ConfirmEmailSchema) => {
    void (async () => {
      try {
        if (isLoadingVerifyEmail || !data.email) return;

        await verifyEmail({
          email: data.email,
          code: data.code,
        });

        clearFlow();
        addToast(t("verifyEmailPage.successInfo"));
        navigate(Routes.DASHBOARD, { replace: true });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleResendCode = () => {
    void (async () => {
      try {
        if (isLoadingVerificationCode || !email) return;

        await resendVerificationCode({
          email,
        });

        setResendCooldown(RESEND_CODE_COOLDOWN_SECONDS);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <div className="fixed inset-0 z-50 flex min-h-[100dvh] overflow-y-auto bg-[var(--cream)] px-5 py-10">
      <main className="m-auto flex w-full max-w-[900px] flex-col items-center text-center">
        <form
          className="flex w-full flex-col items-center"
          onSubmit={handleSubmit(handleVerifyEmail)}
          noValidate
        >
          <div className="mb-8 flex w-full max-w-[720px] flex-col gap-3">
            {isErrorVerifyEmail && (
              <Alert className="font-sans text-sm" status="error">
                {formatErrorMessage(errorVerifyEmail?.message)}
              </Alert>
            )}
            {isErrorVerificationCode && (
              <Alert className="font-sans text-sm" status="error">
                {formatErrorMessage(errorVerificationCode?.message)}
              </Alert>
            )}
            {errors.email && (
              <Alert className="font-sans text-sm" status="error">
                {errors.email.message}
              </Alert>
            )}
          </div>

          <p className="font-sans text-[11px] font-normal uppercase tracking-[0.24em] text-[var(--gold)]">
            {t("verifyEmailPage.eyebrow")}
          </p>
          <h1 className="mt-6 whitespace-pre-line font-serif text-[clamp(42px,5vw,58px)] font-normal leading-[1.35] tracking-[-0.02em] text-[var(--ink)]">
            {isSuccessVerificationCode
              ? t("verifyEmailPage.resentHeader")
              : t("verifyEmailPage.header")}
          </h1>

          <p className="mt-8 max-w-[850px] font-sans text-base leading-[1.5] text-[var(--text-muted)] sm:text-xl">
            {isSuccessVerificationCode
              ? t("verifyEmailPage.resentSubheader")
              : t("verifyEmailPage.subheader")}{" "}
            <span className="text-[var(--gold)]">{email}</span>
            <br />
            {t("verifyEmailPage.subheader1")}
          </p>

          <Controller
            control={control}
            name="code"
            render={(
              {
                field: { onChange, value },
                formState: { errors: fieldErrors },
              },
            ) => (
              <div className="mt-12 flex w-full max-w-[304px] justify-center sm:max-w-[420px] [&>div]:!w-full [&>div]:!justify-between [&>div]:!gap-2 sm:[&>div]:!gap-3 [&_input]:!h-14 [&_input]:!w-11 [&_input]:!rounded-[12px] [&_input]:!border [&_input]:!border-[var(--gold-deep)] [&_input]:!bg-white [&_input]:!font-serif [&_input]:!text-2xl [&_input]:!font-normal [&_input]:!text-[var(--ink)] [&_input]:!outline-none [&_input]:!transition-[border-color,box-shadow] [&_input]:!duration-300 [&_input]:focus:!border-[var(--gold)] [&_input]:focus:!shadow-[0_0_0_3px_rgba(201,168,76,0.18)] sm:[&_input]:!h-16 sm:[&_input]:!w-[60px]">
                <OtpInput
                  isInputNum
                  error={fieldErrors.code?.message}
                  onChange={onChange}
                  value={value}
                  numInputs={6}
                />
              </div>
            )}
          />

          <LoadingButton
            className="journi-primary-button mt-12 h-14 min-h-14 w-full max-w-[540px] rounded-[12px] border-0 !bg-[var(--gold)] font-sans text-base font-medium normal-case text-[var(--ink)] shadow-[0_8px_24px_rgba(180,146,58,0.16)] transition-[transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:!-translate-y-0.5 hover:!bg-[var(--gold-lt)] hover:shadow-[0_12px_28px_rgba(180,146,58,0.24)] active:!translate-y-0 active:!bg-[var(--gold-deep)]"
            loading={isLoadingVerifyEmail}
            type="submit"
          >
            {t("verifyEmailPage.verify")}
          </LoadingButton>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-1.5 font-sans text-base">
            <span className="text-[var(--text-muted)]">
              {t("verifyEmailPage.didntGetIt")}
            </span>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || isLoadingVerificationCode}
              className="text-[var(--gold)] transition-colors duration-300 hover:text-[var(--gold-deep)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resendCooldown > 0
                ? t("button.resendCodeCountdown", { seconds: resendCooldown })
                : t("button.resendCode")}
            </button>
          </div>

          {draftUser && token && (
            <Link
              className="mt-10 rounded-md px-4 py-2 font-sans text-xl font-medium text-[var(--ink)] transition-colors duration-300 hover:text-[var(--gold-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--gold)]"
              onClick={() => setStatus(RegistrationAccountStatus.EDITING)}
              to={Routes.REGISTER}
            >
              {t("button.goBack")}
            </Link>
          )}
        </form>
      </main>
    </div>
  );
};

export default ConfirmEmailPage;
